/**
 * Profile context for NaraHub routes.
 * Manages user profile data including admin status.
 * Also syncs i18n language with profile locale.
 */

import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/AuthContext";
import { getUserProfile, type UserProfile } from "../services/naraDoor";
import i18n, { extractLanguageFromLocale } from "../i18n";

interface ProfileContextType {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Mock profile for development mode
const DEV_MOCK_PROFILE: UserProfile = {
  id: "dev-user-1",
  email: "dev@example.com",
  phone: null,
  firstName: "Dev",
  lastName: "User",
  avatarUrl: null,
  locale: "en-us",
  timezone: "America/New_York",
  isAdmin: true, // Enable admin in dev mode
};

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["profile", user?.uid],
    // In dev mode without a user, use mock data
    enabled: !!user || import.meta.env.DEV,
    queryFn: async (): Promise<UserProfile> => {
      if (!user) return DEV_MOCK_PROFILE;
      try {
        return await getUserProfile();
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        // In dev mode, fall back to mock data on error
        if (import.meta.env.DEV) return DEV_MOCK_PROFILE;
        throw err;
      }
    },
  });

  const profile = query.data ?? null;
  const isLoading = query.isLoading;
  const error = query.error ? (query.error instanceof Error ? query.error.message : "Failed to fetch profile") : null;
  const { refetch } = query;
  const refreshProfile = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Sync i18n language with profile locale
  useEffect(() => {
    if (profile?.locale) {
      const language = extractLanguageFromLocale(profile.locale);
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
        // Also update localStorage for faster initial load on next visit
        try {
          localStorage.setItem('i18nextLng', language);
        } catch {
          // localStorage not available
        }
      }
    }
  }, [profile?.locale]);

  const isAdmin = profile?.isAdmin ?? false;

  return (
    <ProfileContext.Provider
      value={{
        profile,
        isLoading,
        error,
        isAdmin,
        refreshProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
}

